import {
  Phone,
  Check,
  Star,
  Mail,
  Megaphone,
  CalendarClock,
  Clapperboard,
  Play,
  ClipboardList,
  MessageSquare,
  BarChart3,
  MapPin,
  Users,
  Sparkles,
} from "lucide-react";

// Small on-brand "screenshot" mockups that show what each problem/solution
// looks like in practice. Rendered at the bottom of each problem-solver card.

const Panel = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-black/[0.07] bg-white p-3 shadow-[0_8px_24px_-18px_rgba(20,18,16,0.5)] ${className}`}>
    {children}
  </div>
);

const Booked = () => (
  <span className="shrink-0 rounded-full bg-green-500/12 px-2 py-0.5 text-[9px] font-semibold text-green-700">
    Booked
  </span>
);

const CallMock = () => (
  <div className="space-y-2">
    <Panel className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-[11px] font-medium text-[var(--ink)]">
        <Phone size={12} className="text-[var(--ember)]" /> +1 (800) 555-0100
      </span>
      <span className="flex items-center gap-1.5 text-[10px] text-[var(--muted)]">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> AI picked up · 00:09
      </span>
    </Panel>
    <div className="grid grid-cols-2 gap-2">
      {[["Lisa K.", "1:32 PM"], ["James T.", "1:18 PM"]].map(([n, t]) => (
        <Panel key={n} className="flex items-center justify-between">
          <span>
            <span className="block text-[11px] font-semibold text-[var(--ink)]">{n}</span>
            <span className="block text-[9px] text-[var(--muted)]">{t}</span>
          </span>
          <Booked />
        </Panel>
      ))}
    </div>
  </div>
);

const ChatMock = () => (
  <Panel className="space-y-2 p-2.5">
    {[
      ["Sarah M.", "Do you have availability Thurs…"],
      ["James K.", "How much for a consultation?"],
      ["Mike R.", "Are you open on weekends?"],
    ].map(([n, m]) => (
      <div key={n} className="flex items-center justify-between gap-2 border-b border-black/5 pb-2 last:border-0 last:pb-0">
        <span className="flex min-w-0 items-center gap-2">
          <MessageSquare size={12} className="shrink-0 text-[var(--ember)]" />
          <span className="min-w-0">
            <span className="block text-[10px] font-semibold text-[var(--ink)]">{n}</span>
            <span className="block truncate text-[9px] text-[var(--muted)]">{m}</span>
          </span>
        </span>
        <Booked />
      </div>
    ))}
  </Panel>
);

const CampaignMock = () => (
  <Panel className="space-y-2.5">
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-[11px] font-semibold text-[var(--ink)]">
        <Megaphone size={12} className="text-[var(--ember)]" /> New campaign
      </span>
      <span className="rounded-full bg-[var(--ember)]/10 px-2 py-0.5 text-[9px] font-semibold text-[var(--ember)]">Sending</span>
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/5">
      <div className="h-full w-[72%] rounded-full bg-[var(--ember)]" />
    </div>
    <div className="flex justify-between text-[10px] text-[var(--muted)]">
      <span><b className="text-[var(--ink)]">1,240</b> sent</span>
      <span><b className="text-[var(--ink)]">48%</b> opened</span>
      <span><b className="text-[var(--ink)]">92</b> replied</span>
    </div>
  </Panel>
);

const ReviewMock = () => (
  <Panel className="space-y-2">
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/5 text-[9px] font-semibold text-[var(--ink)]">L</span>
        <span className="text-[10px] font-semibold text-[var(--ink)]">Lisa T.</span>
      </span>
      <span className="flex gap-0.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} size={9} className="fill-[#f5a623] text-[#f5a623]" />
        ))}
      </span>
    </div>
    <p className="text-[9px] leading-relaxed text-[var(--muted)]">
      "Amazing service start to finish. Professional and above &amp; beyond."
    </p>
    <div className="rounded-lg bg-[var(--ember)]/[0.06] p-2">
      <span className="flex items-center gap-1 text-[9px] font-semibold text-[var(--ember)]">
        <Sparkles size={9} /> AI Auto-Reply · just now
      </span>
      <p className="mt-1 text-[9px] text-[var(--muted)]">"Thank you, Lisa! Your kind words mean the world."</p>
    </div>
  </Panel>
);

const ScheduleMock = () => (
  <Panel className="space-y-2">
    <div className="flex items-center justify-between text-[11px] font-semibold text-[var(--ink)]">
      <span className="flex items-center gap-2"><CalendarClock size={12} className="text-[var(--ember)]" /> This week</span>
      <span className="text-[9px] font-semibold text-green-700">No-shows ↓ 32%</span>
    </div>
    <div className="grid grid-cols-5 gap-1">
      {["M", "T", "W", "T", "F"].map((d, i) => (
        <div key={i} className="text-center">
          <span className="block text-[9px] text-[var(--muted)]">{d}</span>
          <span className={`mt-1 block h-6 rounded-md ${[1, 2, 4].includes(i) ? "bg-[var(--ember)]/70" : "bg-black/5"}`} />
        </div>
      ))}
    </div>
  </Panel>
);

const ContentMock = () => (
  <Panel className="space-y-2">
    <span className="flex items-center gap-1 text-[9px] font-semibold text-[var(--ember)]">
      <Sparkles size={9} /> AI Generated
    </span>
    <p className="text-[10px] leading-relaxed text-[var(--muted)]">
      Just listed — light-filled 3BR with a chef's kitchen. Book your private tour this weekend. 🏡
    </p>
    <div className="flex gap-1.5">
      {["Reel", "Photo", "Social", "Email"].map((t) => (
        <span key={t} className="rounded-full bg-[var(--ember)]/10 px-2 py-0.5 text-[9px] font-semibold text-[var(--ember)]">{t}</span>
      ))}
    </div>
  </Panel>
);

const VideoMock = () => (
  <Panel className="space-y-2 p-2.5">
    <div className="relative flex h-16 items-center justify-center overflow-hidden rounded-lg bg-[#141210]">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
        <Play size={13} className="ml-0.5 fill-[var(--ember)] text-[var(--ember)]" />
      </span>
      <span className="absolute left-2 top-2 rounded-full bg-black/45 px-2 py-0.5 text-[8px] font-semibold text-white">AI Avatar</span>
      <span className="absolute bottom-2 right-2 rounded-full bg-black/45 px-2 py-0.5 text-[8px] font-medium text-white">0:28</span>
    </div>
    <div className="flex items-center justify-between text-[9px]">
      <span className="font-semibold text-[var(--ink)]">Daily short-form</span>
      <span className="flex items-center gap-1 text-green-700"><Check size={10} /> Auto-published</span>
    </div>
  </Panel>
);

const FormsMock = () => (
  <Panel className="space-y-2">
    <span className="flex items-center gap-2 text-[11px] font-semibold text-[var(--ink)]">
      <ClipboardList size={12} className="text-[var(--ember)]" /> Intake queue
    </span>
    {["New-patient form", "Insurance eligibility", "Records handoff"].map((t) => (
      <div key={t} className="flex items-center justify-between rounded-lg bg-black/[0.03] px-2.5 py-1.5">
        <span className="text-[10px] text-[var(--ink)]">{t}</span>
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-green-500/15">
          <Check size={9} className="text-green-700" />
        </span>
      </div>
    ))}
  </Panel>
);

const StatusMock = () => (
  <Panel className="space-y-2 p-2.5">
    <span className="flex items-center gap-2 text-[10px] font-semibold text-[var(--ink)]">
      <MessageSquare size={12} className="text-[var(--ember)]" /> Client update
    </span>
    <div className="rounded-lg rounded-tl-sm bg-[var(--ember)]/[0.06] p-2">
      <p className="text-[9px] text-[var(--muted)]">Your case has moved to the review stage. We'll update you within 5 days.</p>
    </div>
    <span className="flex items-center gap-1 text-[9px] text-green-700"><Check size={10} /> Auto-sent · saved 6 calls today</span>
  </Panel>
);

const ToolsMock = () => (
  <Panel className="space-y-2">
    <span className="flex items-center gap-2 text-[11px] font-semibold text-[var(--ink)]">
      <BarChart3 size={12} className="text-[var(--ember)]" /> Floor dashboard
    </span>
    <div className="grid grid-cols-3 gap-2">
      {[["Leads", "42"], ["Test drives", "12"], ["Report", "✓"]].map(([l, v]) => (
        <div key={l} className="rounded-lg bg-black/[0.03] p-2 text-center">
          <span className="block text-[13px] font-semibold text-[var(--ember)]">{v}</span>
          <span className="block text-[8px] uppercase tracking-wide text-[var(--muted)]">{l}</span>
        </div>
      ))}
    </div>
  </Panel>
);

const DispatchMock = () => (
  <Panel className="space-y-2">
    <span className="flex items-center gap-2 text-[11px] font-semibold text-[var(--ink)]">
      <MapPin size={12} className="text-[var(--ember)]" /> Today's routes
    </span>
    {[["Tech A", "Job #128 · 9:00"], ["Tech B", "Job #131 · 9:45"], ["Tech C", "Job #135 · 10:30"]].map(([n, j]) => (
      <div key={n} className="flex items-center justify-between rounded-lg bg-black/[0.03] px-2.5 py-1.5">
        <span className="flex items-center gap-2 text-[10px] font-semibold text-[var(--ink)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--ember)]" /> {n}
        </span>
        <span className="text-[9px] text-[var(--muted)]">{j}</span>
      </div>
    ))}
  </Panel>
);

const FunnelMock = () => (
  <Panel className="space-y-2">
    <span className="flex items-center gap-2 text-[11px] font-semibold text-[var(--ink)]">
      <Users size={12} className="text-[var(--ember)]" /> Open-house funnel
    </span>
    <div className="flex items-center gap-1.5">
      {[["Register", "86"], ["Attend", "41"], ["Follow-up", "41"]].map(([l, v], i) => (
        <div key={l} className="flex flex-1 items-center gap-1.5">
          <div className="flex-1 rounded-lg bg-black/[0.03] p-2 text-center">
            <span className="block text-[12px] font-semibold text-[var(--ember)]">{v}</span>
            <span className="block text-[8px] text-[var(--muted)]">{l}</span>
          </div>
          {i < 2 && <span className="text-[var(--ember)]">›</span>}
        </div>
      ))}
    </div>
  </Panel>
);

const MOCKS = {
  phone: CallMock,
  handshake: ChatMock,
  megaphone: CampaignMock,
  star: ReviewMock,
  calendar: ScheduleMock,
  clapperboard: ContentMock,
  video: VideoMock,
  clipboard: FormsMock,
  message: StatusMock,
  wrench: ToolsMock,
  route: DispatchMock,
  users: FunnelMock,
};

export const ProblemVisual = ({ type }) => {
  const Mock = MOCKS[type];
  if (!Mock) return null;
  return (
    <div className="mt-6" data-testid={`problem-visual-${type}`}>
      <Mock />
    </div>
  );
};
